import React, { useState } from 'react';
import { Search, Music, Loader2, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useSpotifySearch } from '@/hooks/useSpotifySearch';
import { SpotifyTrack } from '@/services/spotifyService';

interface SpotifyTrackSelectorProps {
  onTrackSelect: (track: SpotifyTrack) => void;
  selectedTrack: SpotifyTrack | null;
  onClear: () => void;
  placeholder?: string;
  className?: string;
}

const SpotifyTrackSelector: React.FC<SpotifyTrackSelectorProps> = ({
  onTrackSelect,
  selectedTrack,
  onClear,
  placeholder = "Search for a song...",
  className = "",
}) => {
  const [searchInput, setSearchInput] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const { tracks, isLoading, error, searchTracks, clearResults } = useSpotifySearch();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInput(value);
    
    if (value.trim()) {
      searchTracks(value);
      setShowDropdown(true);
    } else {
      clearResults();
      setShowDropdown(false);
    }
  };

  const handleTrackSelect = (track: SpotifyTrack) => {
    onTrackSelect(track);
    setSearchInput(`${track.name} - ${track.artists[0]?.name}`);
    setShowDropdown(false);
    clearResults();
  };

  const handleClear = () => {
    setSearchInput('');
    setShowDropdown(false);
    clearResults();
    onClear();
  };

  const formatDuration = (durationMs: number) => {
    const minutes = Math.floor(durationMs / 60000);
    const seconds = Math.floor((durationMs % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          type="text"
          placeholder={placeholder}
          value={searchInput}
          onChange={handleInputChange}
          className="pl-10 pr-10"
          autoComplete="off"
        />
        {(searchInput || selectedTrack) && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleClear}
            className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 p-0 hover:bg-muted"
          >
            <X className="h-3 w-3" />
          </Button>
        )}
      </div>

      {/* Loading indicator */}
      {isLoading && (
        <div className="absolute right-12 top-1/2 -translate-y-1/2">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Search results dropdown */}
      {showDropdown && (tracks.length > 0 || error) && (
        <Card className="absolute top-full left-0 right-0 z-50 mt-1 max-h-80 overflow-y-auto shadow-lg">
          <CardContent className="p-0">
            {error ? (
              <div className="p-4 text-center text-sm text-red-600">
                <Music className="mx-auto mb-2 h-8 w-8 text-red-400" />
                <p>Unable to search Spotify</p>
                <p className="text-xs text-muted-foreground mt-1">{error}</p>
              </div>
            ) : tracks.length === 0 ? (
              <div className="p-4 text-center text-sm text-muted-foreground">
                <Music className="mx-auto mb-2 h-8 w-8" />
                <p>No songs found</p>
                <p className="text-xs mt-1">Try a different search term</p>
              </div>
            ) : (
              <div className="py-2">
                {tracks.map((track) => (
                  <button
                    key={track.id}
                    onClick={() => handleTrackSelect(track)}
                    className="w-full px-4 py-3 text-left hover:bg-muted/50 transition-colors border-none bg-transparent cursor-pointer"
                  >
                    <div className="flex items-start gap-3">
                      {/* Album cover */}
                      <div className="shrink-0">
                        {track.album.images[0] ? (
                          <img
                            src={track.album.images[2]?.url || track.album.images[0]?.url}
                            alt={track.album.name}
                            className="h-12 w-12 rounded object-cover"
                          />
                        ) : (
                          <div className="h-12 w-12 rounded bg-muted flex items-center justify-center">
                            <Music className="h-6 w-6 text-muted-foreground" />
                          </div>
                        )}
                      </div>

                      {/* Track info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <h4 className="font-medium text-sm truncate">
                              {track.name}
                            </h4>
                            <p className="text-sm text-muted-foreground truncate">
                              {track.artists.map(artist => artist.name).join(', ')}
                            </p>
                            <p className="text-xs text-muted-foreground truncate">
                              {track.album.name}
                            </p>
                          </div>
                          
                          <div className="flex flex-col items-end gap-1 shrink-0">
                            <span className="text-xs text-muted-foreground">
                              {formatDuration(track.duration_ms)}
                            </span>
                            {track.explicit && (
                              <Badge variant="secondary" className="text-xs px-1 py-0">
                                E
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Selected track display */}
      {selectedTrack && !showDropdown && (
        <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-3">
            {selectedTrack.album.images[0] && (
              <img
                src={selectedTrack.album.images[2]?.url || selectedTrack.album.images[0]?.url}
                alt={selectedTrack.album.name}
                className="h-10 w-10 rounded object-cover"
              />
            )}
            <div className="flex-1">
              <p className="font-medium text-sm text-green-800">
                {selectedTrack.name}
              </p>
              <p className="text-sm text-green-600">
                {selectedTrack.artists[0]?.name} • {selectedTrack.album.name}
              </p>
            </div>
            <Badge variant="outline" className="text-green-700 border-green-300">
              Selected
            </Badge>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpotifyTrackSelector; 